import React, { useEffect, useRef } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';

/**
 * Message list for chat UIs (public widget + admin operations).
 */
const ChatWindow = ({
  messages,
  loading = false,
  error = '',
  installationError = '',
  peerTyping = false,
  peerTypingLabel = 'Typing…',
  variant = 'widget',
  emptyMessage = 'No messages yet. Start chatting!',
}) => {
  const messagesEndRef = useRef(null);
  const safeMessages = Array.isArray(messages) ? messages : [];
  const displayError = installationError || error;
  const isAdmin = variant === 'admin';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [safeMessages.length, peerTyping]);

  if (installationError) {
    return (
      <Box
        className="chat-window chat-window--installation-error"
        sx={{
          backgroundColor: '#ffffff',
          color: '#212121',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          maxWidth: 320,
        }}
      >
        <Alert severity="error">{installationError}</Alert>
      </Box>
    );
  }

  return (
    <Box
      className="chat-window"
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: isAdmin ? 2 : 1.25,
        py: 1.5,
        background: isAdmin
          ? 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)'
          : '#fafbfc',
      }}
    >
      {displayError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {displayError}
        </Alert>
      ) : null}

      {safeMessages.length === 0 && !loading && (
        <Typography
          className="no-messages"
          variant="body2"
          sx={{ m: 0, color: 'text.secondary', textAlign: 'center', py: 3 }}
        >
          {emptyMessage}
        </Typography>
      )}

      {safeMessages.map((msg, index) => (
        <ChatMessage
          key={msg?.id || `msg-${index}`}
          message={msg?.text ?? msg?.message ?? ''}
          sender={msg?.sender ?? msg?.senderType ?? 'visitor'}
          senderType={msg?.senderType}
          senderLabel={msg?.senderLabel}
          timestamp={msg?.timestamp}
          variant={variant}
          isInternal={msg?.isInternal === true}
        />
      ))}

      {peerTyping ? <TypingIndicator label={peerTypingLabel} /> : null}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading messages…
          </Typography>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatWindow;
